import type { Backend } from "./contracts";
import { createMemoryBackend } from "./memory/createMemoryBackend";
import { createSupabaseBackend } from "./supabase/createSupabaseBackend";

export interface BackendConfig {
  provider: "memory" | "supabase";
  supabase?: {
    url: string;
    publishableKey: string;
  };
}

type PublicEnv = Pick<ImportMetaEnv, "VITE_BACKEND" | "VITE_SUPABASE_URL" | "VITE_SUPABASE_PUBLISHABLE_KEY">;

export function parseBackendConfig(env: PublicEnv, production: boolean): BackendConfig {
  const configuredProvider = env.VITE_BACKEND;
  const provider = configuredProvider ?? "memory";
  const url = env.VITE_SUPABASE_URL?.trim();
  const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

  if (provider !== "memory" && provider !== "supabase") {
    throw new Error(`Unsupported VITE_BACKEND: ${String(provider)}. Use memory or supabase.`);
  }

  if (provider === "supabase" && url && publishableKey) {
    let parsedUrl: URL;
    try { parsedUrl = new URL(url); } catch { throw new Error("VITE_SUPABASE_URL is not a valid URL."); }
    if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
      throw new Error("VITE_SUPABASE_URL must use http or https.");
    }
    return { provider, supabase: { url, publishableKey } };
  }

  if (provider === "supabase") {
    throw new Error("Supabase mode requires VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.");
  }

  if (production && !configuredProvider) {
    throw new Error("Production builds must set VITE_BACKEND explicitly. Use supabase for production or memory for demos.");
  }

  return { provider: "memory" };
}

export function readBackendConfig(): BackendConfig {
  return parseBackendConfig(import.meta.env, import.meta.env.PROD);
}

export function createBackend(config = readBackendConfig()): Backend {
  if (config.provider === "supabase") {
    if (!config.supabase) {
      throw new Error("Supabase backend requires a URL and publishable key.");
    }
    return createSupabaseBackend(config.supabase);
  }
  return createMemoryBackend();
}
